import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import tinycolor from 'tinycolor2';
import * as uuid from 'uuid';

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { SnAppliance, SnCamera } from '@lib/api/entities';
import { SnCurrentOrganization } from '@lib/auth';
import { SnFormFieldConfig } from '@lib/form';

import { SnCameraScheduleDialogComponent } from '../camera-schedule-dialog';

@Component({
  selector: 'sn-camera-add-rule-dialog',
  templateUrl: './camera-add-rule-dialog.component.html',
  styleUrls: ['./camera-add-rule-dialog.component.scss'],
})
export class CameraAddRuleDialogComponent implements OnDestroy, OnInit {
  public readonly form = new UntypedFormGroup({});
  public formModel: any = {};
  public formFields: SnFormFieldConfig[] = [];

  public detectionOptions;
  public thresholdOptions;

  public schedule: any[] = [
    '* * * * 0',
    '* * * * 1',
    '* * * * 2',
    '* * * * 3',
    '* * * * 4',
    '* * * * 5',
    '* * * * 6',
  ];
  public timezone: string = this._currentOrg.settings.timezone;
  public roi: any[] = [
    [
      [10, 10],
      [10, 20],
      [20, 20],
      [20, 10],
    ],
  ];
  public randomColor: string = this._getRandomColor(25, 150);

  private readonly _destroy$ = new Subject<void>();
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      camera: SnCamera;
      appliance: SnAppliance;
      wrongDirectionRules?: number;
      editingWrongDirection?: boolean;
      rule?: any;
      ruleId?: string;
    },
    private readonly _matDialog: MatDialog,
    private readonly _currentOrg: SnCurrentOrganization,
    private readonly _matDialogRef: MatDialogRef<CameraAddRuleDialogComponent>,
  ) {
    this.detectionOptions = this._determineRules();
  }

  public ngOnInit(): void {
    this._genForm();
    if (this.data.rule) {
      this.updatedFormField();
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public openAiSchedulerDialog(): void {
    const dialog = this._matDialog.open(SnCameraScheduleDialogComponent, {
      data: this.data.camera,
    });
    dialog
      .afterClosed()
      .pipe(
        filter((resp) => !!resp && !!resp.schedules),
        takeUntil(this._destroy$),
      )
      .subscribe(({ timezone, schedules }) => {
        this.schedule = schedules;
        this.timezone = timezone;
      });
  }

  public submitForm(): void {
    const data = {
      alertEnabled: true,
      schedules: this.schedule,
      timezone: this.timezone,
      roi: this.roi,
      id: uuid.v4(),
      color: this.randomColor,
      threshold_duration: 0,
      line_crossing: [],
      ...this.formModel,
      threshold_count: +this.formModel.threshold_count,
    };

    if (!this.data.camera.aiDetectionTypes[0]?.meta) {
      this.data.camera.aiDetectionTypes[0].meta = {};
    }

    if (!this.data.camera.aiDetectionTypes[0].meta?.rules) {
      this.data.camera.aiDetectionTypes[0].meta['rules'] = [];
    }

    const ruleIndex = this.data.camera.aiDetectionTypes[0].meta.rules.findIndex((rule) => {
      return rule.id === this.data.rule?.id;
    });

    if (ruleIndex > -1) {
      const updatedData = {
        alertEnabled: true,
        schedules: this.data.rule.schedule,
        timezone: this.data.rule.timezone,
        id: this.data.rule.id,
        color: this.data.rule.color,
        roi: this.data.rule.roi,
        threshold_duration: 0,
        line_crossing: [],
        ...this.formModel,
        threshold_count: +this.formModel.threshold_count,
      };
      this.data.camera.aiDetectionTypes[0].meta.rules.splice(ruleIndex, 1, updatedData);
    } else {
      this.data.camera.aiDetectionTypes[0].meta.rules.push(data);
    }

    this._matDialogRef.close(this.data.camera);
  }

  public updatedFormField(): void {
    this.formModel['name'] = this.data.rule?.name;
    this.formModel['type'] = this.data.rule?.type;
    this.formModel['labels'] = this.data.rule?.labels;
  }

  private _genForm(): void {
    this.formFields = [
      {
        key: 'name',
        type: 'input',
        templateOptions: {
          label: 'Name',
          floatLabel: 'always',
          required: true,
          placeholder: 'Enter Name',
        },
      },
      {
        key: 'type',
        type: 'select',
        templateOptions: {
          label: 'Type',
          floatLabel: 'always',
          required: true,
          placeholder: 'Select Type',
          options: this.detectionOptions,
          change: (field, event) => {
            this._hideSearchFields(field, event);
          },
        },
      },
      {
        key: 'threshold_count',
        type: 'input',
        hide: true,
        defaultValue: 1,
        templateOptions: {
          label: 'Threshold',
          floatLabel: 'always',
          type: 'number',
          min: 1,
          required: true,
        },
      },
      {
        key: 'labels',
        type: 'select',
        templateOptions: {
          floatLabel: 'always',
          infinityScroll: true,
          options: this.data.camera.aiDetectionTypes[0].allowedLabels,
          placeholder: 'Select Label',
          valueProp: (v) => v,
          labelProp: (v) => v,
          emptyOption: true,
          emptyLabel: 'any',
          multiple: true,
          label: 'Label',
          clearable: true,
        },
      },
    ];
  }

  private _getRandomColor(min: number, max: number): string {
    const color = {
      r: 10,
      g: Math.floor(Math.random() * (max - min) + min),
      b: Math.floor(Math.random() * (max - min) + min),
    };

    return tinycolor(color).toHexString();
  }

  private _hideSearchFields(field: any, event: any): void {
    const thresholdIndex = field.parent.fieldGroup.findIndex((group) => {
      return group.key === 'threshold_count';
    });
    event.value === 'crowd-detection'
      ? (field.parent.fieldGroup[thresholdIndex].hide = false)
      : this._resetThreshold(thresholdIndex, field);
  }

  private _resetThreshold(thresholdIndex: number, field: any): void {
    field.parent.fieldGroup[thresholdIndex].hide = true;
    this.formModel.threshold_count = 1;
  }

  private _determineRules(): any[] {
    const options = [];
    const allowWrongDirection =
      this._currentOrg.settings.isDemo || this.data.appliance?.settings?.allowWrongDirection;
    if (
      (this.data.wrongDirectionRules < 1 || this.data.editingWrongDirection) &&
      allowWrongDirection
    ) {
      options.push(
        { label: 'Wrong Direction', value: 'wrong-direction' },
        { label: 'Unauthorized Object', value: 'unauthorized-object' },
        { label: 'Crowd Detection', value: 'crowd-detection' },
      );
    } else {
      options.push(
        { label: 'Unauthorized Object', value: 'unauthorized-object' },
        { label: 'Crowd Detection', value: 'crowd-detection' },
      );
    }
    return options;
  }
}
